import React from 'react';
import { motion } from 'framer-motion';
import {MapPin, Train, DollarSign, Clock, Utensils} from 'lucide-react';
import { useScrollAnimation, useParallax } from '../../hooks/useScrollAnimation';

export default function Singapore() {
  const scrollY = useParallax();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation(0.2);
  const { ref: districtsRef, isVisible: districtsVisible } = useScrollAnimation(0.2);
  const { ref: attractionsRef, isVisible: attractionsVisible } = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="relative h-[400px] overflow-hidden">
        <motion.img
          src="https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Singapore"
          className="w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/80 to-transparent" />
        <motion.div 
          className="absolute inset-0 flex items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1 
              className="text-5xl font-bold text-white mb-2"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Singapore
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              The Lion City – Modern Marvel Meets Cultural Fusion
            </motion.p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[
            { icon: Clock, color: '#4A90E2', label: 'Recommended Stay', value: '3-4 days' },
            { icon: DollarSign, color: '#F7B32B', label: 'Daily Budget', value: 'SGD 80-150' },
            { icon: Train, color: '#FF6B35', label: 'Best Transport', value: 'MRT (Metro)' },
            { icon: Utensils, color: '#0A2540', label: 'Must-Try', value: 'Hawker Center Food' }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="bg-white rounded-lg p-4 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
            >
              <item.icon className="w-6 h-6 mb-2" style={{ color: item.color }} />
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="font-semibold text-gray-800">{item.value}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <motion.div
              ref={contentRef}
              initial={{ opacity: 0, x: -30 }}
              animate={contentVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-[#0A2540] mb-4">About Singapore</h2>
              <p className="text-gray-600 mb-4">
                A gleaming metropolis where East meets West, Singapore is a city-state that punches far above its weight. From futuristic Gardens by the Bay to historic temples, from Michelin-starred restaurants to affordable hawker centers, it's a perfect blend of old and new.
              </p>
              <p className="text-gray-600 mb-8">
                Clean, efficient, safe, and incredibly diverse – Singapore offers world-class shopping, dining, and attractions in a compact, easy-to-navigate package.
              </p>
            </motion.div>

            <div ref={districtsRef}>
              <motion.h3 
                className="text-2xl font-bold text-[#0A2540] mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={districtsVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
              >
                Must-Visit Districts
              </motion.h3>
              <div className="space-y-4 mb-8">
                {[
                  { name: 'Marina Bay', desc: 'Iconic skyline, Gardens by the Bay, Marina Bay Sands' },
                  { name: 'Chinatown', desc: 'Historic temples, street food, traditional shops' },
                  { name: 'Little India', desc: 'Vibrant culture, colorful streets, authentic cuisine' },
                  { name: 'Orchard Road', desc: 'Premier shopping district, luxury malls' },
                  { name: 'Sentosa Island', desc: 'Beaches, Universal Studios, resorts' }
                ].map((area, idx) => (
                  <motion.div 
                    key={idx} 
                    className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF6B35]"
                    initial={{ opacity: 0, x: -30 }}
                    animate={districtsVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    whileHover={{ x: 5, boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }}
                  >
                    <h4 className="font-semibold text-gray-800 mb-1">{area.name}</h4>
                    <p className="text-sm text-gray-600">{area.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div ref={attractionsRef}>
              <motion.h3 
                className="text-2xl font-bold text-[#0A2540] mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={attractionsVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
              >
                Top Attractions
              </motion.h3>
              <ul className="space-y-2 text-gray-700">
                {[
                  { name: 'Gardens by the Bay', desc: 'Futuristic gardens with Supertrees' },
                  { name: 'Marina Bay Sands', desc: 'Iconic hotel with rooftop infinity pool' },
                  { name: 'Universal Studios Singapore', desc: 'Southeast Asia\'s top theme park' },
                  { name: 'Singapore Zoo', desc: 'World-renowned open-concept zoo' },
                  { name: 'Merlion Park', desc: 'Singapore\'s iconic symbol' }
                ].map((attr, idx) => (
                  <motion.li 
                    key={idx}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    animate={attractionsVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>{attr.name}:</strong> {attr.desc}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-lg mb-6"
              whileHover={{ y: -5, boxShadow: '0 15px 35px rgba(0,0,0,0.1)' }}
            >
              <h3 className="text-xl font-bold text-[#0A2540] mb-4">Travel Tips</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>Download the SingPass app for digital entry</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>EZ-Link card for MRT/bus travel</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>No chewing gum allowed</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>Hawker centers offer cheap, delicious food</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
